import { useEffect, useRef } from 'react';

export function useWatermarkProtection({
  wrapperSelector = '.watermark-protection',
  watermarkSelector = '.w-watermark',
  onTamper = () => {
    window.location.href = "/login";
  }
} = {}) {

  const expectedStylesRef = useRef({
    position: 'absolute',
    top: '0px',
    left: '0px',
    zIndex: '5', // Match your component zIndex
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    display: 'block',
    visibility: 'visible'
  });

  //original watermark text
  const originalTextsRef = useRef(null);

  useEffect(() => {
    let debounceTimeout = null;

    const isTranslationActive = () => {
      const html = document.documentElement;
      return (
        html.classList.contains('translated-ltr') ||
        html.classList.contains('translated-rtl')
      );
    };

    const enforceStyles = (el) => {
      const expected = expectedStylesRef.current;
      let tampered = false;

      // Only loop through critical security styles
      Object.entries(expected).forEach(([key, value]) => {
        if (el.style[key] !== value) {
          el.style[key] = value; 
          tampered = true;
        }
      });
      return tampered;
    };

    const checkTamper = (mutationsList) => {
      if (debounceTimeout) clearTimeout(debounceTimeout);

      debounceTimeout = setTimeout(() => {
        const wrapper = document.querySelector(wrapperSelector);
        if (!wrapper) return;

        const watermark = wrapper.querySelector(watermarkSelector);

        // 1. Check if watermark was deleted
        if (!watermark) {
          onTamper();
          return;
        }

        // 2. Check if the mutations actually affected the watermark
        const isWatermarkMutation = mutationsList.some(mutation =>
          mutation.target === watermark || watermark.contains(mutation.target)
        );

        if (!isWatermarkMutation) return;

        const translationActive = isTranslationActive();

        // HANDLE TRANSLATION CASE
        if (translationActive) {
          const mutationCount = mutationsList.length;
          const hasFontTag = mutationsList.some(m =>
            m.target.nodeName === 'FONT' || m.target.parentNode?.nodeName === 'FONT'
          );

          const isCharacterOnly = mutationsList.every(
            m => m.type === 'characterData'
          );

          // many changes OR FONT wrapping
          if (mutationCount > 5 || hasFontTag || isCharacterOnly) {
            return;
          }
          onTamper();
          return;
        }

        // NORMAL (NON-TRANSLATOR)
        // Direct text edit detection — characterData fires when
        const hasCharacterDataChange = mutationsList.some(
          (m) => m.type === 'characterData'
        );

        if (hasCharacterDataChange) {
          onTamper();
          return;
        }

          const style = window.getComputedStyle(watermark);
          if (
            style.display === 'none' ||
            style.visibility === 'hidden' ||
            parseFloat(style.opacity) < 0.1
          ) {
            onTamper();
            return;
          }

        //Text content validation against original snapshot
        const textEls = watermark.querySelectorAll('div');
        const currentTexts = Array.from(textEls).map(
          (el) => el.textContent?.trim()
        );

        if (originalTextsRef.current) {
          const textChanged = originalTextsRef.current.some(
            (original, i) =>
              original &&
              currentTexts[i] !== undefined &&
              currentTexts[i] !== original
          );
          if (textChanged) {
            onTamper();
            return;
          }
        }

        //Empty text check
        const hasEmptyText = Array.from(textEls).some(
          (el) =>
            el.children.length === 0 &&
            (!el.textContent || el.textContent.trim() === '')
        );
        if (hasEmptyText) {
          onTamper();
          return;
        }

        // Structure check
        if (textEls.length === 0) {
            onTamper();
            return;
          }

          // Style enforcement
          const healed = enforceStyles(watermark);
          if (healed) {
            onTamper();
        }
      }, 100);
    };

    const observer = new MutationObserver(checkTamper);

    const wrapperNode = document.querySelector(wrapperSelector);
    if (wrapperNode) {

      // Snapshot original watermark text
      const watermark = wrapperNode.querySelector(watermarkSelector);
      if (watermark) {
        // Wait for React to render watermark text
        setTimeout(() => {
          const textEls = watermark.querySelectorAll('div');
          originalTextsRef.current = Array.from(textEls).map(
            (el) => el.textContent?.trim()
          );
          console.log("Watermark snapshot taken:", originalTextsRef.current);
        }, 500);
      }

      observer.observe(wrapperNode, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden'],
        characterData: true, 
        characterDataOldValue: true,
      });
    }

    return () => {
      observer.disconnect();
      if (debounceTimeout) clearTimeout(debounceTimeout);
    };
  }, [wrapperSelector, watermarkSelector, onTamper]);
}